import { CatalogItemTagIcons } from '../../components/CatalogItemTag'
import { type ConversationTelemetryEvent } from '../../value-objects/ConversationTelemetryEvent'
import {
  type ConversationComponent,
  type ConversationEnvelopeItem,
  type ConversationMessageSender,
} from '../../value-objects/ConversationTurnItem'
import {
  ConversationComponentTypes,
  ConversationItemKinds,
} from '../../value-objects/enums'
import {
  type ConversationComponentApiModel,
  type ConversationEnvelopeApiModel,
  type ConversationStreamEventApiModel,
} from '../api-models/ConversationEventApiModel'

const normalizeSender = (sender: string): ConversationMessageSender =>
  sender === 'user' ? 'user' : 'llm'

function mapConversationComponent(
  component: ConversationComponentApiModel
): Nullable<ConversationComponent> {
  switch (component.type) {
    case ConversationComponentTypes.MESSAGE:
      return {
        type: ConversationComponentTypes.MESSAGE,
        sender: normalizeSender(component.params.sender),
        content: component.params.content,
        ...(component.params.tags && { tags: component.params.tags }),
      }
    case ConversationComponentTypes.CONFIRMATION_OPTIONS:
      return {
        type: ConversationComponentTypes.CONFIRMATION_OPTIONS,
        options: component.params.options,
      }
    case ConversationComponentTypes.GROUPED_DROPDOWN:
      return {
        type: ConversationComponentTypes.GROUPED_DROPDOWN,
        groups: component.params.groups.map((group) => ({
          id: group.id,
          name: group.name,
          options: group.options.map((option) => ({
            id: option.id,
            name: option.name,
          })),
        })),
        groupLabel: component.params.group_label,
        optionLabel: component.params.option_label,
        defaultGroupId: component.params.default_group_id ?? null,
        defaultOptionId: component.params.default_option_id ?? null,
      }
    case ConversationComponentTypes.CATALOG_ITEM: {
      const { img, title, body, price, tags } = component.params
      const moreInformation = component.params.more_information
      const specification = moreInformation?.specification ?? []
      const sku =
        specification.find(([label]) => label.toLowerCase() === 'sku')?.[1] ??
        ''

      return {
        type: ConversationComponentTypes.CATALOG_ITEM,
        itemId: sku,
        ...(img && { img }),
        title,
        body,
        sku,
        price: price.price,
        currency: price.currency,
        tags: tags.map((tag) => ({
          icon: CatalogItemTagIcons.CATALOG,
          text: tag.text,
        })),
        agentAnalysis: moreInformation?.agent_analysis?.body ?? '',
        catalogDetails: specification.map(([label, value]) => ({
          label,
          value,
        })),
      }
    }
    default:
      return null
  }
}

export const mapConversationEnvelopeEvent = (
  envelope: ConversationEnvelopeApiModel
): Omit<ConversationEnvelopeItem, 'id'> => ({
  kind: ConversationItemKinds.ENVELOPE,
  envelopeId: envelope.id,
  components: envelope.components
    .map(mapConversationComponent)
    .filter(
      (component): component is ConversationComponent => component !== null
    ),
  isAnswered: false,
})

export const mapConversationTelemetryEvent = (
  event: ConversationStreamEventApiModel
): ConversationTelemetryEvent => ({
  type: event.type,
  data: event.data,
  timestamp: event.timestamp ?? null,
})
